/**
 * i18n (docs/architecture.md §11). Strings live in locales/<lang>/*.json, one file per area
 * (common, fight, hud, screens), merged into one tree, plus a pseudo-locale for testing layouts
 * (?lang=pseudo): accented and ~40% longer. All 39 languages are translated (D23).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANGUAGES, NEEDS_FALLBACK_FONT } from './languages';

export const PSEUDO = 'pseudo';

type Tree = { [k: string]: string | Tree };

function merge(into: Tree, from: Tree): Tree {
  for (const [k, v] of Object.entries(from)) {
    const cur = into[k];
    into[k] = typeof v === 'object' && typeof cur === 'object' ? merge({ ...cur }, v) : v;
  }
  return into;
}

const enParts = import.meta.glob<{ default: Tree }>('./locales/en/*.json', { eager: true });
const EN: Tree = Object.values(enParts).reduce<Tree>((acc, m) => merge(acc, m.default), {});

const otherParts = import.meta.glob<{ default: Tree }>(['./locales/*/*.json', '!./locales/en/*.json']);

const ACCENTS: Record<string, string> = {
  a: 'á', e: 'é', i: 'í', o: 'ö', u: 'ü', c: 'ç', n: 'ñ', s: 'š', y: 'ý', z: 'ž',
  A: 'Å', E: 'É', I: 'Í', O: 'Ø', U: 'Û', C: 'Ç', N: 'Ñ', S: 'Š', Y: 'Ý', Z: 'Ž',
};

function pseudoString(s: string): string {
  // Keep {{placeholders}} and <tags> intact.
  const parts = s.split(/(\{\{[^}]+\}\}|<[^>]+>)/g);
  const body = parts
    .map((p) => (p.startsWith('{{') || p.startsWith('<') ? p : p.replace(/[a-zA-Z]/g, (c) => ACCENTS[c] ?? c)))
    .join('');
  const pad = '·'.repeat(Math.ceil(s.length * 0.4));
  return `⟦${body}${pad}⟧`;
}

function pseudoTree(t: Tree): Tree {
  const out: Tree = {};
  for (const [k, v] of Object.entries(t)) out[k] = typeof v === 'string' ? pseudoString(v) : pseudoTree(v);
  return out;
}

function initialLanguage(saved?: string): string {
  const param = new URLSearchParams(location.search).get('lang');
  if (param) return param;
  if (saved) return saved;
  for (const l of navigator.languages ?? []) {
    const code = l.toLowerCase().split('-')[0] ?? '';
    if (LANGUAGES.some((x) => x.code === code)) return code;
  }
  return 'en';
}

export async function initI18n(savedLanguage?: string): Promise<void> {
  const lng = initialLanguage(savedLanguage);
  await i18n.use(initReactI18next).init({
    resources: {
      en: { translation: EN },
      [PSEUDO]: { translation: pseudoTree(EN) },
    },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  await applyLanguage(lng);
}

/**
 * Language codes whose texts follow a regional variant (D23): i18next and Intl run on the full
 * tag, so European Portuguese gets its plural rules (0 takes the plural, unlike Brazil) and its
 * number formats. The files and the saved setting keep the short code.
 */
const REGIONS: Record<string, string> = { pt: 'pt-PT' };

/** Whether strings exist for a language (M1: English only; others fall back to English). */
export function isTranslated(code: string): boolean {
  return code === 'en' || Object.keys(otherParts).some((p) => p.startsWith(`./locales/${code}/`));
}

/** Switches language, loading its strings and fallback font when needed. */
export async function applyLanguage(code: string): Promise<void> {
  const tag = REGIONS[code] ?? code;
  if (code !== 'en' && code !== PSEUDO && !i18n.hasResourceBundle(tag, 'translation')) {
    const loaders = Object.entries(otherParts).filter(([p]) => p.startsWith(`./locales/${code}/`));
    if (loaders.length) {
      const parts = await Promise.all(loaders.map(([, load]) => load()));
      i18n.addResourceBundle(tag, 'translation', parts.reduce<Tree>((acc, m) => merge(acc, m.default), {}));
    }
  }
  if (NEEDS_FALLBACK_FONT.has(code)) {
    await import('@fontsource/m-plus-rounded-1c/800.css');
    await import('@fontsource/m-plus-rounded-1c/900.css');
  }
  document.documentElement.lang = code === PSEUDO || !isTranslated(code) ? 'en' : tag;
  await i18n.changeLanguage(tag);
}

export default i18n;
